import React, { useState, useEffect, useContext } from "react";
import * as StyleDOM from "../../../../Components/Applicant-Tracking/Candidates/CandidateDetails/CandidateDocuments.jsx";
import AnimationPlayer from "../../../../Common/Animation-Player/AnimationPlayer";
import VybogTALLoaderAnimation from "../../../../Common/Dynamic-Animations/DynamicAnimations.js";
import NoHistoryAnimation from "../../../../Assets/Json/No-History-Animation.json";
import mailIcon from "../../../../Assets/Images/mail_icon.png";
import phoneIcon from "../../../../Assets/Images/phone_icon.png";
import locationViewIcon from "../../../../Assets/Images/location_icon.svg";
import Button from "../../../../Common/Button/Button";
import TextArea from "../../../../Common/TextArea/TextArea";
import SelectInput from "../../../../Common/Select-Input/SelectInput.js";
import DynamicAutocomplete from "../../../../Common/AutoSuggestion/DynamicAutocomplete.js";
import { post } from "../../../../helper/Api_helper";
import { VYLABS_BASE_URL, CLIENT_TRACKING } from "../../../../helper/url_helper";
import {
  getStageStyles,
  formatDateTime,
  getRecruiterName
} from "../../../../Common/Common-Function/CommonFunctions.js";
import staticOptions from "../../../../Assets/Json/StaticOptions.json";
import DynamicReminder from "../../../../Common/Dynamic-Reminder/DynamicReminder.jsx";
import CallbackReminderDisplay from "../../../../Common/Callback-Reminder-UI/CallbackReminder.js";
import ClientEmailHistory from "./ClientEmailHistory.js";
import "../../Candidates/Candidates.css";
import { DynamicContext } from "../../../../Context/DynamicContext.js";


const ClientTracking = ({
  clientId,
  clientPriority,
  clientStatus,
  selectedClient,
  contactOptions,
  setSelectedContactId: setParentSelectedContactId,
  editFormik,
  handleTriggerClear: parentHandleTriggerClear,
  clearTriggered,
  setClearTriggered,
  trackingRefreshKey,
  isDarkMode,
  reminderFormik,
  editRemainderFormik,
  showReminderDialog,
  onOpenReminderDialog,
  onCloseReminderDialog,
  onSubmitReminder,
  fetchCallbackReminders,
  callbackRemainderRes,
  handleDeleteCallbackRemainder,
  isHiringTeamTenant
}) => {
  const { formatDateWithUserTimezone } = useContext(DynamicContext);
  const { StaticOptions = {} } = staticOptions;
  const { callbackReminderOffset = [] } = StaticOptions || {};
  const [clientTracking, setClientTracking] = useState([]);
  const [showContactTracking, setShowContactTracking] = useState(false);
  const [contactFilteredTracking, setContactFilteredTracking] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [filteredContactOptions, setFilteredContactOptions] =
    useState(contactOptions);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [historyViewTab, setHistoryViewTab] = useState(0);
  const [hasEmailHistory, setHasEmailHistory] = useState(false);

  const fetchClientTracking = async (customOffset = 0, contactId = null) => {
    if (!clientId) {
      console.warn("No client selected, skipping fetchClientTracking");
      return;
    }
    try {
      if (customOffset === 0) {
        setIsInitialLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const payload = {
        clientID: clientId,
        offset: customOffset,
        limit: 100,
      };
      if (contactId || selectedContactId) {
        payload.ContactID = contactId || selectedContactId;
      }

      const cliTrackingresponse = await post(
        VYLABS_BASE_URL,
        CLIENT_TRACKING,
        payload,
      );

      const trackingLogs = cliTrackingresponse?.tracking_logs ?? [];
      const trackingNewValues = trackingLogs.map((log) => ({
        id: log.id,
        user_id: log.user_id,
        first_name: log.first_name,
        last_name: log.last_name,
        created_at: log.created_at,
        new_value: log.new_value,
        contact_id: log.contact_id,
      }));

      if (contactId || selectedContactId) {
        setContactFilteredTracking(trackingNewValues);
      } else {
        setClientTracking((prev) => {
          if (customOffset === 0) {
            return trackingNewValues;
          } else {
            const existingApplications = new Map();
            (prev || []).forEach((track) => {
              if (track?.id) existingApplications.set(track.id, track);
            });
            trackingNewValues.forEach((track) => {
              if (track?.id) existingApplications.set(track.id, track);
            });
            return Array.from(existingApplications.values());
          }
        });
      }
    } catch (err) {
      console.error("fetchClientTracking error", err);
      if (contactId || selectedContactId) {
        setContactFilteredTracking([]);
      } else {
        setClientTracking((prev) => prev || []);
      }
    } finally {
      setIsInitialLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleContactFilterChange = async (contactId) => {
    setSelectedContactId(contactId);
    setParentSelectedContactId(contactId);
    if (contactId) {
      setShowContactTracking(true);
      await fetchClientTracking(0, contactId);
    } else {
      setShowContactTracking(false);
      setContactFilteredTracking([]);
      await fetchClientTracking(0, null);
    }
  };

  useEffect(() => {
    if (!clientId) return;
    fetchClientTracking();
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !trackingRefreshKey) return;
    fetchClientTracking(0, selectedContactId || null);
  }, [trackingRefreshKey]);

  useEffect(() => {
    setFilteredContactOptions(contactOptions);
  }, [contactOptions]);

  const isEditReminderMode = Boolean(callbackRemainderRes?.callbackAt);
  const activeReminderFormik = isEditReminderMode
    ? editRemainderFormik
    : reminderFormik;

  const contacts = selectedClient?.contacts || [];
  const hasContacts = contacts.length > 0;
  const displayTracking = showContactTracking
    ? contactFilteredTracking
    : clientTracking;
  const isTrackingLoading =
    isInitialLoading || isLoadingMore || editFormik.isSubmitting;
  const selectedContactObj = selectedContactId
    ? contactOptions.find((opt) => opt.value === selectedContactId)
    : null;
  const selectedContactDetails = selectedContactObj?.contact || null;
  const defaultContactLocation = [
    selectedClient?.city,
    selectedClient?.state,
    selectedClient?.country,
  ]
    .filter(Boolean)
    .join(", ");
  const viewingContact = selectedContactDetails
    ? {
        name: selectedContactDetails.name,
        designation: selectedContactDetails.designation,
        email: selectedContactDetails.email,
        phone: selectedContactDetails.phone,
        location: selectedContactDetails.location,
      }
    : {
        name: selectedClient?.contact_person,
        designation: selectedClient?.contact_designation,
        email: selectedClient?.email,
        phone: selectedClient?.phone,
        location: defaultContactLocation,
      };
  const viewingContactName = viewingContact.name || "N/A";
  const viewingContactInitials =
    viewingContactName !== "N/A"
      ? viewingContactName
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((name) => name[0])
          .join("")
          .toUpperCase()
      : "NA";
  const shouldShowHistoryHeading =
    historyViewTab === 0 ? displayTracking.length > 0 : hasEmailHistory;

  useEffect(() => {
    fetchCallbackReminders();
  }, []);

  return (
    <div className="tracing-tab-container">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "0px",
        }}
      >
        {shouldShowHistoryHeading ? (
          <StyleDOM.TrackingHistoryHeader style={{ margin: 0 }}>
            <h3
              style={{
                color: "var(--header__color)",
                fontSize: "16px",
                fontWeight: 700,
              }}
            >
              {historyViewTab === 0 ? "Track History" : "Email History"}
            </h3>
          </StyleDOM.TrackingHistoryHeader>
        ) : (
          <div />
        )}
        <div className="filter-stages" style={{ display: "inline-flex" }}>
          <Button
            label="Track History"
            iconPosition="left"
            iconStyle={{ marginRight: "8px" }}
            event={() => setHistoryViewTab(0)}
            className={`grid-btn left ${historyViewTab === 0 ? "active" : ""}`}
          />
          <Button
            label="Email History"
            iconPosition="left"
            iconStyle={{ marginRight: "8px" }}
            event={() => setHistoryViewTab(1)}
            className={`grid-btn right ${historyViewTab === 1 ? "active" : ""}`}
          />
        </div>
      </div>
      {historyViewTab === 1 ? (
        <ClientEmailHistory
          clientId={clientId}
          onHasEmailHistoryChange={setHasEmailHistory}
        />
      ) : (
        <>
          <div>
            <div className="client-tracking-maininnercontainer">
              <div className="upperoutercontainer">
                <div className="tracking-history-toolbar">
                  <div>
                    <div className="tracking-history-title">
                      Tracking History
                    </div>
                    <p className="tracking-history-subtitle">
                      Communication, calls and notes captured against this
                      client.
                    </p>
                  </div>
                  {hasContacts && !isHiringTeamTenant && (
                    <div className="contact-filter-section">
                      <span className="contact-filter-label">View</span>
                      <div className="contact-filter-controls">
                        <DynamicAutocomplete
                          label=""
                          options={filteredContactOptions}
                          value={selectedContactObj || null}
                          onChange={(newValue) => {
                            const contactId = newValue?.value || "";
                            editFormik.setFieldValue(
                              "editContactId",
                              contactId,
                            );
                            handleContactFilterChange(contactId);
                          }}
                          onInputChange={(_, inputValue) => {
                            if (inputValue) {
                              const filtered = contactOptions.filter((option) =>
                                option.label
                                  .toLowerCase()
                                  .includes(inputValue.toLowerCase()),
                              );
                              setFilteredContactOptions(filtered);
                            } else {
                              setFilteredContactOptions(contactOptions);
                            }
                          }}
                          placeholder="Search and select contact person..."
                          slotProps={{
                            paper: {
                              sx: {
                                backgroundColor: "var(--body__background)",
                                color: "var(--header__color)",
                                borderRadius: "8px",
                                boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                                border: "1px solid var(--border__color)",
                              },
                            },
                            listbox: {
                              sx: {
                                "& li": {
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  padding: "8px 16px",
                                  color: "var(--header__color)",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                },
                              },
                            },
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiInputBase-root": {
                              height: "40px",
                              color: "var(--header__color)",
                              borderRadius: "8px",
                              "& .MuiOutlinedInput-notchedOutline": {
                                border:
                                  "1px solid var(--border__color) !important",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#6860ee",
                                },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#6860ee",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "var(--header__color)",
                              fontSize: "14px",
                            },
                          }}
                          noOptionsText="No contacts available"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="tracking-viewing-details">
                  <div
                    className="tracking-viewing-label"
                    style={{ fontSize: "14px", fontWeight: 600 }}
                  >
                    Viewing
                  </div>
                  <div className="tracking-viewing-grid">
                    <div className="tracking-viewing-person">
                      <div className="tracking-contact-avatar">
                        {viewingContactInitials}
                      </div>
                      <div>
                        <div className="tracking-contact-name">
                          {viewingContactName}
                        </div>
                        <div className="tracking-contact-designation">
                          {viewingContact.designation || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="tracking-viewing-item">
                      <img
                        src={mailIcon}
                        alt="email"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <div>
                        <h4 className="info-header-value">Email</h4>
                        <div className="tracking-viewing-item-value">
                          <h5
                            className="client-details-value"
                            style={{ marginTop: "3px" }}
                          >
                            {" "}
                            {viewingContact.email || "N/A"}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="tracking-viewing-item">
                      <img
                        src={phoneIcon}
                        alt="phone"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <div>
                        <h4 className="info-header-value">Phone</h4>
                        <div className="tracking-viewing-item-value">
                          <h5
                            className="client-details-value"
                            style={{ marginTop: "3px" }}
                          >
                            {" "}
                            {viewingContact.phone || "N/A"}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="tracking-viewing-item">
                      <img
                        src={locationViewIcon}
                        alt="location"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <div>
                        <h4 className="info-header-value">Location</h4>
                        <div className="tracking-viewing-item-value">
                          <h5
                            className="client-details-value"
                            style={{ marginTop: "3px" }}
                          >
                            {viewingContact.location || "N/A"}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="job-div">
                  <div className="textfield-container">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        className="detail-label"
                        style={{
                          marginBottom: "0",
                          fontSize: "16px",
                          fontWeight: 800,
                        }}
                      >
                        Add Tracking Entry
                      </span>
                      <p
                        className="tracking-history-subtitle"
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          fontWeight: 500,
                          marginTop: "2px",
                        }}
                      >
                        For (
                        <span className="tracking-entry-contact-chip">
                          <span className="tracking-entry-contact-name">
                            {viewingContactName}
                          </span>{" "}
                          -
                          <span className="tracking-entry-contact-designation">
                            {viewingContact.designation || "N/A"}
                          </span>
                        </span>
                        )
                      </p>
                    </div>
                    <span
                      className="detail-label"
                      style={{ marginBottom: "8px" }}
                    >
                      Remarks
                    </span>
                    <TextArea
                      placeholder={"Enter Remarks"}
                      className="ai-search-textfield"
                      name="editRemarks"
                      value={editFormik.values.editRemarks}
                      onChange={editFormik.handleChange}
                    />
                  </div>
                  <div
                    className="job-auto"
                    style={{ display: "flex", gap: "20px" }}
                  >
                    <div
                      className="textfield-container"
                      style={{ width: "100%" }}
                    >
                      <span
                        className="detail-label"
                        style={{ marginBottom: "10px" }}
                      >
                        Priority
                      </span>
                      <SelectInput
                        options={clientPriority}
                        name="editClientPriority"
                        value={editFormik.values.editClientPriority}
                        onChange={editFormik.handleChange}
                        labelStyle={{ marginBottom: "10px" }}
                        className="detail-label"
                      />
                    </div>
                    <div
                      className="textfield-container"
                      style={{ width: "100%" }}
                    >
                      <span
                        className="detail-label"
                        style={{ width: "fit-content", marginBottom: "10px" }}
                      >
                        {isHiringTeamTenant ? "Activity" : "Company Status"}
                      </span>
                      <SelectInput
                        options={clientStatus}
                        name="editClientStatus"
                        value={editFormik.values.editClientStatus}
                        onChange={editFormik.handleChange}
                        labelStyle={{ marginBottom: "10px" }}
                        className="detail-label"
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      marginTop: "2%",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      label="Clear"
                      isCancel={true}
                      event={() => {
                        parentHandleTriggerClear(editFormik, setClearTriggered);
                      }}
                      style={{
                        height: "32px",
                        width: "66px",
                        borderRadius: "8px",
                        border: "1px solid #6860ee",
                      }}
                    />
                    <Button
                      label="Add Track"
                      event={editFormik.handleSubmit}
                      disabled={isTrackingLoading}
                      style={{
                        height: "32px",
                        width: "96px",
                        borderRadius: "8px",
                        fontFamily: "Satoshi",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        textAlign: "center",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <StyleDOM.ContentMainContainer className="contentMain-Container">
            {isTrackingLoading ? (
              <div className="client-tracking-loader">
                <VybogTALLoaderAnimation />
              </div>
            ) : displayTracking.length > 0 ? (
              <>
                {callbackRemainderRes?.callbackAt && (
                  <CallbackReminderDisplay
                    reminder={callbackRemainderRes}
                    isDarkMode={isDarkMode}
                    onEdit={onOpenReminderDialog}
                  />
                )}
                <StyleDOM.TrackingHistoryHeader
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {showContactTracking ? (
                    <>
                      Tracking History For{" "}
                      <span style={{ color: "#6860ee" }}>
                        {selectedContactObj
                          ? `( ${selectedContactObj.label} )`
                          : ""}
                      </span>
                    </>
                  ) : (
                    "Tracking History"
                  )}
                  {!callbackRemainderRes?.callbackAt && (
                    <Button
                      label={"Set Reminder"}
                      event={onOpenReminderDialog}
                    />
                  )}
                </StyleDOM.TrackingHistoryHeader>
                <StyleDOM.TrackingLowerDisplayContainer className="display-container">
                  {displayTracking.map((tracking, index) => (
                    <StyleDOM.TrackingLowerContainer
                      className={
                        displayTracking.length > 1
                          ? "tracking-lowercontainer"
                          : ""
                      }
                      key={tracking.id || index}
                    >
                      <StyleDOM.TrackingMainInnerContainer className="tracking-inner">
                        <StyleDOM.TrackingLowerInnerContainer className="lower-innercontainer">
                          <StyleDOM.TrackingContentDiv>
                            <StyleDOM.ContentDate className="content-date">
                              {formatDateWithUserTimezone(
                                tracking.new_value.updated_at,
                              )}
                            </StyleDOM.ContentDate>
                            <StyleDOM.ContentStatus
                              style={getStageStyles(tracking.new_value.status)}
                            >
                              {tracking.new_value.status ||
                                "No status available"}
                            </StyleDOM.ContentStatus>
                          </StyleDOM.TrackingContentDiv>
                        </StyleDOM.TrackingLowerInnerContainer>
                        <StyleDOM.ContentContainer className="contentcontainer">
                          <StyleDOM.ContentDescription className="contentdescription">
                            <StyleDOM.Content>
                              {tracking.new_value.remarks ||
                                "No remarks available for this tracking"}
                            </StyleDOM.Content>
                          </StyleDOM.ContentDescription>
                        </StyleDOM.ContentContainer>
                        <StyleDOM.RecruiterTag>
                          Recruiter: {getRecruiterName(tracking)}
                        </StyleDOM.RecruiterTag>
                      </StyleDOM.TrackingMainInnerContainer>
                    </StyleDOM.TrackingLowerContainer>
                  ))}
                </StyleDOM.TrackingLowerDisplayContainer>
              </>
            ) : (
              <StyleDOM.NoTrackingHistory className="no-tracking-history">
                <h4 style={{ fontSize: "16px", fontWeight: 600 }}>
                  There is no tracking history available for{" "}
                  <span style={{ color: "#6860ee" }}>{viewingContactName}</span>
                  .
                </h4>
                <AnimationPlayer
                  animationSrc={NoHistoryAnimation}
                  playerHeight="200px"
                  playerWidth="300px"
                />
              </StyleDOM.NoTrackingHistory>
            )}
          </StyleDOM.ContentMainContainer>
        </>
      )}

      {activeReminderFormik && (
        <DynamicReminder
          isOpen={showReminderDialog}
          onClose={onCloseReminderDialog}
          onSubmit={onSubmitReminder}
          reminderFormik={activeReminderFormik}
          callbackReminderOffset={callbackReminderOffset}
          isDarkMode={isDarkMode}
          isEditMode={isEditReminderMode}
          handleDeleteCallbackRemainder={handleDeleteCallbackRemainder}
        />
      )}
    </div>
  );
};

export default ClientTracking;